import React, { useEffect, useState } from "react";
import MuiPaper from "Components/MuiPaper/MuiPaper";
import Translator from "Languages/Utility/Translator";
import { satc_priceFactor } from "Constants/TableColumns";
import RemoteTable from "Components/RemoteTable/RemoteTable";
import { useTranslation } from 'react-i18next';
import { useSetState } from "Helpers/Hooks";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import Grid from "@material-ui/core/Grid";
import { read } from "Helpers/LocalStorage";
import { Skeleton } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import { getAccountingPriceFactor, getActivePeriods, getFactorCombo } from "Stores/actions";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import AddPriceFactors from "./AddPriceFactors";
import PageTitle from "Components/PageTitle_IconCircle/PageTitle";

export default function Factors() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tblData, setTblData] = useState();
  const [columns, setColumns] = useState([]);
  const { accountPriceFactor, activePeriods, factorCombo, getPriceFactorAgain, pagination } = useSelector((state) => ({
    accountPriceFactor: state.saAccounts.accountPriceFactor,
    activePeriods: state.saAccounts.activePeriods,
    factorCombo: state.saAccounts.factorCombo,
    getPriceFactorAgain: state.saAccounts.getPriceFactorAgain,
    pagination: state.saAccounts.pagination
  }));
  const [controls, setControls] = useSetState({
    periods: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
      display: true
    },
    factors: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
      display: false
    },
  });

  useEffect(() => {
    onEnter();
  },[])

  const onEnter = async () => {
    setLoading(true);
    const _columns = [...satc_priceFactor];
    _columns[8].hidden = !read("FormPriceSuperAdmin").accessUpdate;
    _columns[9].hidden = !read("FormPriceSuperAdmin").accessDelete;
    setColumns(_columns);
    await getPeriods();
    await getFactorCombos();
    await getPrices(1, sizePerPage);
  }

  const getPrices = async (_page, _sizePerPage, dataSearch = null) => {
    setLoading(true);
    return new Promise(async resolve => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
        Limit: _sizePerPage,
        Page: _page,
        ...dataSearch
      }
      await dispatch(getAccountingPriceFactor(params));
      setLoading(false);
      resolve(true);
    })
  }

  const getPeriods = async () => {
    return new Promise(async resolve => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      }
      await dispatch(getActivePeriods(params));
      resolve(true);
    })
  }

  const getFactorCombos = async () => {
    return new Promise(async resolve => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      }
      await dispatch(getFactorCombo(params));
      resolve(true);
    })
  }

  useEffect(() => {
    if(accountPriceFactor) {
      setTblData(accountPriceFactor);
      setSizePerPage(pagination.Limit);
      setCurrentPage(pagination.Page);
      setTotal(pagination.Total);
    }
  },[accountPriceFactor])
  
  useEffect(() => {
    if(activePeriods) {
      const defArr = [{ label: t('chooseOnePeriod'), value: -1}];
      const selectItems = activePeriods.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ periods: { selectItems: defArr.concat(selectItems) } });
      setControls({ periods: { value: defArr[0] } });
    }
  },[activePeriods])

  useEffect(() => {
    if(factorCombo) {
      const defArr = [{ label: t('chooseOneFactor'), value: -1}];
      const selectItems = factorCombo.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ factors: { selectItems: defArr.concat(selectItems) } });
      setControls({ factors: { value: defArr[0] } });
    }
  },[factorCombo])

  const onTableChange = async (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      await tblRefresh(page, sizePerPage);
    }
  }

  const autoCompleteHandler = async (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
    await tblRefresh(1, sizePerPage, 
      controlName === 'factors' ? value.value : null,
      controlName === 'periods' ? value.value : null);
  }

  useEffect(() => {
    if(getPriceFactorAgain) {
      (async () => {
        await tblRefresh(currentPage, sizePerPage);
      })()
    }
  },[getPriceFactorAgain])

  const tblRefresh = async (innerPage, innerSizePerPage, factor = null, period = null) => {
    let dataSearch = {};
    const { factors, periods } = controls;
    if(factor) {
      if(factor !== -1)
        dataSearch.FactorId = factor;
      if(periods.value.value !== -1)
        dataSearch.PeriodId = periods.value.value;
    } else if(period) {
      if(period !== -1)
        dataSearch.PeriodId = period;
      if(factors.value.value !== -1)
        dataSearch.FactorId = factors.value.value;
    } else {
      if(factors.value.value !== -1)
        dataSearch.FactorId = factors.value.value;
      if(periods.value.value !== -1)
        dataSearch.PeriodId = periods.value.value;
    }
    await getPrices(innerPage, innerSizePerPage, dataSearch);
  }

  return (
    <>
      <BackDropLoading loading={loading} />
      <div className="page-top-title-button my-3">
        <PageTitle title={<Translator id={"factors"} />} />
        <div className="top-buttons">
          <AddPriceFactors disabled={loading} />
        </div>
      </div>
      <MuiPaper>
        <Grid container className="mb-2">
          <Grid item md={3} sm={6} xs={12}>
            {!loading ? (
              <MuiAutoComplete
                autoSelect={true}
                options={controls.periods.selectItems}
                label={<Translator id="periods" />}
                value={controls.periods.value}
                onChange={autoCompleteHandler}
                id={"periods"}
              />
            ) : (
              <Skeleton variant="text" className="p-2" animation="wave" />
            )}
          </Grid>
        </Grid>
        {columns && columns.length !== 0 && (
          <RemoteTable
            data={tblData}
            columns={columns}
            page={currentPage}
            sizePerPage={sizePerPage}
            totalSize={total}
            onTableChange={onTableChange}
          />
        )}
      </MuiPaper>
    </>
  );
}